import { ClientTypeContext, DatasourceFieldHelper, INERT_CLIENT_TYPES } from "../src/taskpane/components/helpers/dataSourceFieldHelper";
import { IKeyNamePair } from "../src/taskpane/interfaces/IKeyNamePair";
import { ITenantInfo } from "../src/taskpane/interfaces/ITenantInfo";
import { boundField, dataSourceItem, SiteItem, TenantState } from "../src/taskpane/redux/models/reduxModels";
import { apiGetTenantInfo } from "../src/taskpane/services/ApiService";
import authModule from "../utilities/AuthModule";
import { logger } from "../utilities/logger";

class AdvancedSearchStateManager {
    dataSourceItems: dataSourceItem[];
    sites: SiteItem[];
    // boundedFields: boundField[];
    allowedAdvancedSearchFields: string[];
    allowedSiteCollections: string[];
    allowedTeams: string[];
    enablePII?: boolean;
    tenantEnableApprovalHooks?: boolean;
    enableRedaction?: boolean;
    isDataSourcePanelActive: boolean = false;
    replaceFileNameWithGUID?: boolean;
    disableFileRename?: boolean;
    openDocumentsInClientApp?: boolean;
    enableSharing?: boolean;
    datasourceFields?: boundField[];
    appLogo?: string;
    appColor?: string;
    enableAdvSearchMultiSites?: boolean;
    disableAdvSearchContentTypes?: boolean;
    clientListUrl?: string;

    async getTenantInfo(tenantId?: string, tenantState?: TenantState): Promise<void> {
        try {
            if (!tenantState) {
                const tenantInfo: ITenantInfo = await apiGetTenantInfo(tenantId);
                this.dataSourceItems = tenantInfo.datasources;
                this.sites = tenantInfo.sites;

                // this.boundedFields = tenantInfo.datasources
                //     ?.map(item => item.boundFields)
                //     .reduce((acc, current) => {
                //         return acc.concat(current);
                //     }, [])
                //     ?? [];

                this.allowedAdvancedSearchFields = tenantInfo.allowedAdvancedSearchFields?.toLowerCase()
                    .split(";")
                    .map(allowedField => allowedField.trim())
                    .filter(allowedField => allowedField.length > 0);

                this.allowedSiteCollections = tenantInfo.allowedSiteCollections?.toLowerCase()
                    .split(";")
                    .map(allowedSite => allowedSite.trim())
                    .filter(allowedSite => allowedSite.length > 0);

                this.allowedTeams = tenantInfo.allowedTeams?.toLowerCase()
                    .split(";")
                    .map(allowedTeam => allowedTeam.trim())
                    .filter(allowedTeam => allowedTeam.length > 0);

                this.enablePII = tenantInfo.enablePII;
                this.tenantEnableApprovalHooks = tenantInfo.tenantEnableApprovalHooks;
                this.enableRedaction = tenantInfo.enableRedaction;
                this.replaceFileNameWithGUID = tenantInfo.replaceFileNameWithGUID;
                this.disableFileRename = tenantInfo.disableFileRename;
                this.openDocumentsInClientApp = tenantInfo.openDocumentsInClientApp;
                this.enableSharing = tenantInfo.enableSharing;
                this.appColor = tenantInfo.appColor;
                this.appLogo = tenantInfo.appLogo;
                this.enableAdvSearchMultiSites = tenantInfo.enableAdvSearchMultiSites;
                this.disableAdvSearchContentTypes = tenantInfo.disableAdvSearchContentTypes;
                this.clientListUrl = tenantInfo.clientListUrl;
            } else {
                this.dataSourceItems = tenantState.datasources;
                this.sites = tenantState.sites;

                // this.boundedFields = tenantState.datasources
                //     ?.map(item => item.boundFields)
                //     .reduce((acc, current) => {
                //         return acc.concat(current);
                //     }, [])
                //     ?? [];

                this.allowedAdvancedSearchFields = tenantState.allowedAdvancedSearchFields;
                this.allowedSiteCollections = tenantState.allowedSiteCollections;
                this.allowedTeams = tenantState.allowedTeams;

                this.enablePII = tenantState.enablePII;
                this.tenantEnableApprovalHooks = tenantState.tenantEnableApprovalHooks;
                this.enableRedaction = tenantState.enableRedaction;
                this.replaceFileNameWithGUID = tenantState.replaceFileNameWithGUID;
                this.disableFileRename = tenantState.disableFileRename;
                this.openDocumentsInClientApp = tenantState.openDocumentsInClientApp;
                this.enableSharing = tenantState.enableSharing;
                this.enableAdvSearchMultiSites = tenantState.enableAdvSearchMultiSites;
                this.disableAdvSearchContentTypes = tenantState.disableAdvSearchContentTypes;
                this.clientListUrl = tenantState.clientListUrl;
            }
        } catch (error) {
            logger(error, "advancedSearchStateManager.ts (getTenantInfo)");
        }
    };

    setDatasourceFields(
        selectedSites: IKeyNamePair[],
        selectedLibraries: IKeyNamePair[],
        clientTypes: ClientTypeContext = INERT_CLIENT_TYPES,
    ): void {
        // Single output array, nullish boundFields skipped - the old map + reduce-concat
        // appended a literal `undefined` element for a datasource without bound fields,
        // which crashed the input factory's `.filter(item => item.name && ...)` and took
        // the whole Advanced Search form down. Mirrors the hardened taskpane collection
        // (DatasourceFieldHelper.getDatasourceFields).
        const fields: dataSourceItem["boundFields"] = [];
        for (const item of this.dataSourceItems) {
            if (this.checkDatasourceAllowed(selectedSites, selectedLibraries, item, clientTypes)
                && Array.isArray(item?.boundFields)) {
                fields.push(...item.boundFields);
            }
        }
        this.datasourceFields = fields;
    }

    // Delegates to the single shared implementation. This surface evaluates the restriction
    // twice - here to decide which control renders, and again in getInputDataSource to decide
    // which datasource that control queries - so two copies of the rules could render a
    // datasource dropdown that then resolves nothing.
    private checkDatasourceAllowed(
        selectedSites: IKeyNamePair[],
        selectedLibraries: IKeyNamePair[],
        datasource: dataSourceItem,
        clientTypes: ClientTypeContext,
    ): boolean {
        return DatasourceFieldHelper.checkDatasourceAllowed(
            (selectedSites ?? []).map(site => site?.value ?? ""),
            (selectedLibraries ?? []).map(library => library?.value ?? ""),
            datasource,
            clientTypes,
        );
    }
}

const advancedSearchStateManager = new AdvancedSearchStateManager();
export default advancedSearchStateManager;